import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/accounts/')({
  component: AccountsIndexPage,
})

type AccountRow = {
  id: string
  name?: string | null
  website?: string | null
  industryId?: string | null
  ownerUserId?: string | null
  createdAt?: string | Date | null
}

type LookupRow = { id: string; name: string }

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return '-'
  }
}

function AccountsIndexPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery(
    orpc.crm.accounts.list.queryOptions({ input: { limit: 500, offset: 0 } }),
  )
  const rows: AccountRow[] = (data as AccountRow[] | undefined) ?? []

  // Industry lookup (for display mapping id -> name).
  const { data: industryData } = useQuery(
    orpc.crm.lookups.list.queryOptions({ input: { table: 'industries' } }),
  )
  const industryMap = useMemo(() => {
    const m = new Map<string, string>()
    const rowsLookup = (industryData as LookupRow[] | undefined) ?? []
    for (const r of rowsLookup) m.set(r.id, r.name)
    return m
  }, [industryData])

  const reassignMutation = useMutation(
    orpc.crm.accounts.reassign.mutationOptions({
      onError: (e) => toast.error(e.message),
    }),
  )

  const columns: EntityListColumn<AccountRow>[] = [
    {
      key: 'name',
      header: t('crm.account.name'),
      render: (r) => <span className="font-medium">{r.name ?? '(unnamed)'}</span>,
    },
    { key: 'website', header: t('crm.account.website'), render: (r) => r.website ?? '-' },
    {
      key: 'industry',
      header: t('crm.account.industry'),
      render: (r) => (r.industryId ? (industryMap.get(r.industryId) ?? '-') : '-'),
    },
    { key: 'owner', header: t('crm.common.ownedBy'), render: (r) => r.ownerUserId ?? '-' },
    {
      key: 'created',
      header: t('crm.common.createdAt'),
      render: (r) => formatDate(r.createdAt ?? null),
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('crm.account.entityPlural')}</h1>
      </div>

      <EntityList
        rows={rows}
        columns={columns}
        searchAccessor={(r) => `${r.name ?? ''} ${r.website ?? ''}`}
        searchPlaceholder={`${t('crm.common.search')}…`}
        isLoading={isLoading}
        newButton={{
          label: t('crm.account.newAccount'),
          onClick: () => navigate({ to: '/crm/accounts/new' }),
        }}
        onRowClick={(r) =>
          navigate({ to: '/crm/accounts/$accountId', params: { accountId: r.id } })
        }
        bulkActions={[
          {
            key: 'reassign',
            label: t('crm.common.reassign'),
            onClick: async (selected) => {
              // accounts router exposes per-record `reassign` only; we fan out
              // the requested selection client-side so the bulk UX matches the
              // leads list (Phase 6 follow-up: a proper bulkReassign procedure).
              const target = window.prompt('Reassign to user id:')
              if (!target) return
              let ok = 0
              for (const row of selected) {
                try {
                  await reassignMutation.mutateAsync({ id: row.id, ownerUserId: target })
                  ok++
                } catch {
                  // individual error toast already fired in onError
                }
              }
              toast.success(`${ok} ${t('crm.account.entityPlural').toLowerCase()}`)
              queryClient.invalidateQueries({ queryKey: orpc.crm.accounts.list.key() })
            },
          },
        ]}
      />
    </div>
  )
}
